import { LaunchAttempt } from '../IntersectEvent/mobileWebDiagnostics';
import { mobileWebTelemetryPayload } from '../IntersectEvent/mobileWebTelemetry';

export function classifyUnityFailure(value: unknown): string {
  const text = typeof value === 'string' ? value : value instanceof Error ? value.message : '';
  if (/out of memory|cannot enlarge memory|allocation failed/i.test(text)) return 'out-of-memory';
  if (/memory access out of bounds/i.test(text)) return 'wasm-memory-access';
  if (/device.*lost|gpu.*lost/i.test(text)) return 'gpu-device-lost';
  if (/script error\.?$/i.test(text.trim())) return 'redacted-script-error';
  if (/failed to load|failed to fetch|networkerror/i.test(text)) return 'resource-load';
  if (/abort|runtimeerror|unreachable/i.test(text)) return 'wasm-runtime';
  return 'unknown';
}

/** Report once per Unity launch, without sending the error text, stack, URLs or credentials. */
export function createUnityFailureReporter(accessToken: string, upcUrl: string, apiUrl = process.env.REACT_APP_API_URL) {
  const startedAt = new Date().toISOString();
  const started = Date.now();
  const launchId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    let n: number;
    try { n = crypto.getRandomValues(new Uint8Array(1))[0] & 15; }
    catch (_) { n = Math.floor(Math.random() * 16); }
    return (c === 'x' ? n : (n & 3) | 8).toString(16);
  });
  let reported = false;
  return (error: unknown) => {
    if (reported) return;
    reported = true;
    if (!apiUrl || !accessToken) return;
    // Hash the build URL for correlation; never transmit query parameters or the URL itself.
    void (async () => {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(upcUrl));
      const upcRef = Array.from(new Uint8Array(digest)).map(v => v.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const attempt: LaunchAttempt = {
        schemaVersion: 2, launchId, startedAt, voiceDisabled: false,
        browser: { userAgent: navigator.userAgent, viewport: [window.innerWidth, window.innerHeight],
          devicePixelRatio: window.devicePixelRatio || 1, secureContext: window.isSecureContext },
        runtime: { upcRef, unityFailureCategory: classifyUnityFailure(error) },
        milestones: [{ sequence: 1, stage: 'player-failed', elapsedMs: Math.max(0, Date.now() - started) }],
      };
      await fetch(`${apiUrl.replace(/\/$/, '')}/intersect/mobile-web-diagnostics`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(mobileWebTelemetryPayload(attempt, 'current')),
      });
    })().catch(() => { /* Reporting must never prevent recovery. */ });
  };
}
